import isEmpty from 'lodash/isEmpty'
import axios from 'axios'
import {
  ManualPrescriptionObject,
  PrescriptionFlow,
  PrescriptionObject,
  UploadPrescriptionObject,
} from '@/scripts/libs/rox-lens-panel/types/PrescriptionModule'
import wcs from '@/scripts/libs/wcs'
import Prescription from '@/scripts/libs/prescription'
import sha from 'sha.js'
import makeDebug from 'debug'

const debug = makeDebug('wcs-prescription')

interface WcsManualPrescription {
  prescriptionFlow: PrescriptionFlow.MANUAL
  prescriptionId?: number
  orderId: number
  orderItemId: number
  lsphreading?: number
  lcycreading?: number
  laxisreading?: number
  ladddetails?: number
  rsphreading?: number
  rcycreading?: number
  raxisreading?: number
  radddetails?: number
  pupdistance?: number
  rpupdistance?: number
  lpupdistance?: number
  rhprysm?: number
  rhprysmbase?: string
  rvprysm?: number
  rvprysmbase?: string
  lhprysm?: number
  lhprysmbase?: string
  lvprysm?: number
  lvprysmbase?: string
  COMMENT?: string
  issueDate?: string
  doctorName?: string
}

interface WcsUploadPrescription {
  prescriptionFlow: string
  orderId?: number
  orderItemId?: number
  prescriptionId?: number
  uploadPrescriptionFile: string
  fileName: string
  fileSize: string
  pupdistance?: number
  rpupdistance?: number
  lpupdistance?: number
}

interface IWCSPrescription {
  saveUploadPrescription: (
    _uploadPrescriptionObject: UploadPrescriptionObject,
    _orderId: number,
    _orderItemId: number
  ) => Promise<boolean>
  getUploadPrescription: (
    _orderId: number,
    _orderItemId: number
  ) => Promise<UploadPrescriptionObject>
  saveManualPrescription: (
    _manualPrescription: ManualPrescriptionObject,
    _orderId: number,
    _orderItemId: number
  ) => Promise<boolean>
  getManualPrescription: (
    _orderId: number,
    _orderItemId: number
  ) => Promise<ManualPrescriptionObject>

  getPrescription: (_orderId: number, _orderItemId: number) => Promise<PrescriptionObject>

  clearPrescription: (_orderId: number, _orderItemId: number) => Promise<boolean>

  normalizePrescription: (
    _prescriptionObject: PrescriptionObject
  ) => WcsManualPrescription | WcsUploadPrescription
  enablePrism: () => boolean
}

class WCSPrescription implements IWCSPrescription {
  saveUploadPrescription = async (
    uploadPrescriptionObject: UploadPrescriptionObject,
    orderId: number,
    orderItemId: number
  ) => {
    const wcsUploadPrescriptionObject =
      this.normalizeUploadPrescriptionToWcs(uploadPrescriptionObject)

    const params: WcsUploadPrescription = {
      orderId,
      orderItemId,
      ...wcsUploadPrescriptionObject,
    }
    const endpoint = wcs.getEndPoint('roxUploadPrescription')

    try {
      const response = await axios.post<WcsUploadPrescription>(endpoint, params)
      return !!response
    } catch (error) {
      debug(error)
      return false
    }
  }
  getUploadPrescription = async (orderId: number, orderItemId: number) => {
    const endpoint = wcs.getEndPoint('roxUploadPrescription')
    const crypto = this.getCrypto(orderId, orderItemId, endpoint)

    const params = { orderId, orderItemId, crypto }

    try {
      const response = await axios.get<WcsUploadPrescription>(endpoint, { params })
      const uploadPrescriptionObject = this.normalizeUploadPrescriptionFromWcs(response.data)
      return uploadPrescriptionObject
    } catch (error) {
      debug(error)
      return {} as UploadPrescriptionObject
    }
  }
  saveManualPrescription = async (
    manualPrescription: ManualPrescriptionObject,
    orderId: number,
    orderItemId: number
  ) => {
    const wcsManualPrescriptionObject = this.normalizeManualPrescriptionToWcs(manualPrescription)
    const params: WcsManualPrescription = {
      ...wcsManualPrescriptionObject,
      orderId: orderId,
      orderItemId,
    }
    const endpoint = wcs.getEndPoint('roxManualPrescription')
    const response = await axios.post<WcsManualPrescription>(endpoint, params)
    return !!response
  }
  getManualPrescription = async (orderId: number, orderItemId: number) => {
    const endpoint = wcs.getEndPoint('roxManualPrescription')
    const crypto = this.getCrypto(orderId, orderItemId, endpoint)

    const params = { orderId, orderItemId, crypto }

    try {
      const response = await axios.get<WcsManualPrescription>(endpoint, { params })
      const manualPrescriptionObject = this.normalizeManualPrescriptionFromWcs(response.data)
      return manualPrescriptionObject
    } catch (error) {
      debug(error)
      return {} as ManualPrescriptionObject
    }
  }

  getPrescription = async (orderId: number, orderItemId: number) => {
    const manualPrescriptionObject = await this.getManualPrescription(orderId, orderItemId)
    if (!isEmpty(manualPrescriptionObject)) {
      return manualPrescriptionObject
    }

    const uploadPrescriptionObject = await this.getUploadPrescription(orderId, orderItemId)
    return uploadPrescriptionObject
  }

  normalizePrescription = (prescriptioObject: PrescriptionObject) => {
    if (Prescription.isUploadPrescriptionObject(prescriptioObject)) {
      return this.normalizeUploadPrescriptionToWcs(prescriptioObject)
    }

    return this.normalizeManualPrescriptionToWcs(prescriptioObject as ManualPrescriptionObject)
  }

  clearPrescription = async (orderId: number, orderItemId: number) => {
    const params = { orderId, orderItemId }
    const endpoint = wcs.getEndPoint('roxManualPrescription')
    const response = await axios.delete(endpoint, { params })
    return !!response
  }

  private normalizeManualPrescriptionFromWcs = (manualPrescription: WcsManualPrescription) => {
    const manualPrescriptionObject: ManualPrescriptionObject = {
      prescriptionFlow: PrescriptionFlow.MANUAL,
      prescriptionId: manualPrescription.prescriptionId,
      PD: {
        OD: manualPrescription.rpupdistance ?? manualPrescription.pupdistance,
        OS: manualPrescription.lpupdistance,
      },
      SPH: {
        OD: manualPrescription.rsphreading,
        OS: manualPrescription.lsphreading,
      },
      CYL: {
        OD: manualPrescription.rcycreading,
        OS: manualPrescription.lcycreading,
      },
      AX: {
        OD: manualPrescription.raxisreading,
        OS: manualPrescription.laxisreading,
      },
      ADD: {
        OD: manualPrescription.radddetails,
        OS: manualPrescription.ladddetails,
      },
      //TODO: check how to define PRISM_ENABLED parameter
      PRISM_ENABLED: manualPrescription.rhprysm ? true : false,
      VPRISM: {
        OD: manualPrescription.rvprysm,
        OS: manualPrescription.lvprysm,
      },
      VBASEDIR: {
        OD: manualPrescription.rvprysmbase,
        OS: manualPrescription.lvprysmbase,
      },
      HPRISM: {
        OD: manualPrescription.rhprysm,
        OS: manualPrescription.lhprysm,
      },
      HBASEDIR: {
        OD: manualPrescription.rhprysmbase,
        OS: manualPrescription.lhprysmbase,
      },
      COMMENT: manualPrescription.COMMENT,
      issueDate: manualPrescription.issueDate,
      doctorName: manualPrescription.doctorName,
    }
    return manualPrescriptionObject
  }
  private normalizeUploadPrescriptionFromWcs = (
    uploadPrescriptionResponse: WcsUploadPrescription
  ) => {
    const uploadPrescriptionObject: UploadPrescriptionObject = {
      fileData: uploadPrescriptionResponse?.uploadPrescriptionFile,
      prescriptionFlow: PrescriptionFlow.UPLOAD,
      prescriptionId: uploadPrescriptionResponse?.prescriptionId,
      savedFileName: uploadPrescriptionResponse?.fileName,
      PD: {
        OD: uploadPrescriptionResponse?.rpupdistance,
        OS: uploadPrescriptionResponse?.lpupdistance,
      },
      fileName: uploadPrescriptionResponse?.fileName,
      fileSize: uploadPrescriptionResponse?.fileSize,
    }
    return uploadPrescriptionObject
  }
  private normalizeManualPrescriptionToWcs = (manualPrescription: ManualPrescriptionObject) => {
    if (!manualPrescription) {
      return {} as WcsManualPrescription
    }
    const pupillaryDistanceIsSingleValue = !manualPrescription.PD?.OS && !!manualPrescription.PD?.OD
    const normalizedObject: WcsManualPrescription = {
      prescriptionFlow: PrescriptionFlow.MANUAL,
      prescriptionId: manualPrescription.prescriptionId,
      orderId: 0,
      orderItemId: 0,
      lsphreading: manualPrescription.SPH?.OS,
      lcycreading: manualPrescription.CYL?.OS,
      laxisreading: manualPrescription.AX?.OS,
      ladddetails: manualPrescription.ADD?.OS,
      rsphreading: manualPrescription.SPH?.OD,
      rcycreading: manualPrescription.CYL?.OD,
      raxisreading: manualPrescription.AX?.OD,
      radddetails: manualPrescription.ADD?.OD,
      pupdistance: pupillaryDistanceIsSingleValue ? manualPrescription.PD?.OD : undefined,
      rpupdistance: pupillaryDistanceIsSingleValue ? undefined : manualPrescription.PD?.OD,
      lpupdistance: pupillaryDistanceIsSingleValue ? undefined : manualPrescription.PD?.OS,
      rhprysm: manualPrescription.HPRISM?.OD,
      rhprysmbase: manualPrescription.HBASEDIR?.OD,
      rvprysm: manualPrescription.VPRISM?.OD,
      rvprysmbase: manualPrescription.VBASEDIR?.OD,
      lhprysm: manualPrescription.HPRISM?.OS,
      lhprysmbase: manualPrescription.HBASEDIR?.OS,
      lvprysm: manualPrescription.VPRISM?.OS,
      lvprysmbase: manualPrescription.VBASEDIR?.OS,
      COMMENT: manualPrescription.COMMENT ?? '',
      issueDate: manualPrescription.issueDate ?? '',
      doctorName: manualPrescription.doctorName ?? '',
    }
    return normalizedObject
  }
  private normalizeUploadPrescriptionToWcs = (uploadPrescription: UploadPrescriptionObject) => {
    const commonProperties = {
      prescriptionFlow: PrescriptionFlow.UPLOAD,
      prescriptionId: uploadPrescription.prescriptionId,
      uploadPrescriptionFile: uploadPrescription.fileData,
      fileName: uploadPrescription.fileName,
      fileSize: uploadPrescription.fileSize,
    }
    const normalizedObject: WcsUploadPrescription =
      uploadPrescription.PD.OS && uploadPrescription.PD.OD
        ? {
            ...commonProperties,
            rpupdistance: uploadPrescription.PD.OD,
            lpupdistance: uploadPrescription.PD.OS,
          }
        : {
            ...commonProperties,
            pupdistance: uploadPrescription.PD.OD,
          }

    return normalizedObject
  }

  private getCrypto = (orderId: number, orderItemId: number, endpoint: string) => {
    const { storeId } = wcs.getStoreConfig()
    const salt = `wcsPrescServ | ${endpoint}?orderId=${orderId}&orderItemId=${orderItemId}`
    const pattern = `${storeId} ${orderItemId} ${orderId} ${orderItemId} ${storeId}`

    const crypto = sha('sha256').update(`${salt}${pattern}`).digest('hex')
    return crypto
  }

  enablePrism = (): boolean => {
    const { roxLensPanel } = wcs.getStoreConfig()
    return roxLensPanel?.enablePrism
  }
}

export default new WCSPrescription()
